import React, { useState } from 'react';
import PdfEditor from '../PdfEditor';
import { getBondVers, ensureBondReal, bondFileName, bondKey, BONDDOCS } from './bonddoc';
import { openS3 } from './docgen';
import { uploadDoc, fetchFileBlob } from './api';
import { UpLabel } from './stepcommon';
import type { Project, CoCard, DocVerMap, DocVer } from './types';
import { saveToProject, savedMsg } from './projectFolder';

interface Props {
  p: Project;
  co: CoCard;
  k: string; // 'bondsub' | 'bondcontract'
  onPatch: (patch: Partial<Project>) => void;
  toast: (m: string) => void;
}
type RepKind = 'edit' | 'seal' | 'pdfonly';

export default function BondDocRow({ p, co, k, onPatch, toast }: Props) {
  const [busy, setBusy] = useState('');
  const [editUrl, setEditUrl] = useState<string | null>(null);
  const seal = !!BONDDOCS[k].seal;
  const ck = bondKey(co.id, k);
  const vers = getBondVers(p, co, k);
  const cur = vers[vers.length - 1];
  const real = !!(cur.s3doc || cur.s3pdf);

  const pv = async () => {
    if (busy) return;
    setBusy('pv');
    try {
      const v = await ensureBondReal(p, co, k, onPatch, toast);
      if (!v.s3pdf) { toast('PDF가 없습니다 — 재생성하세요'); return; }
      await openS3(v.s3pdf, bondFileName(co, k, 'pdf'), true);
    } catch (e) { toast((e as Error).message); } finally { setBusy(''); }
  };
  const dl = async (fmt: 'doc' | 'pdf') => {
    if (busy) return;
    setBusy(fmt);
    const name = bondFileName(co, k, fmt === 'doc' ? 'docx' : 'pdf');
    try {
      const folder = await saveToProject(p.id, name, async () => {   // 조합 저장 폴더로(2026-11-01)
        const v = await ensureBondReal(p, co, k, onPatch, toast);
        const key = fmt === 'doc' ? v.s3doc : v.s3pdf;
        if (!key) throw new Error(fmt === 'doc' ? 'Word 파일이 없습니다' : 'PDF 변환 실패 버전입니다 — 재생성하거나 PDF를 직접 올려주세요');
        return fetchFileBlob(key, name);
      });
      toast(savedMsg(folder, name));
    } catch (e) { toast((e as Error).message); } finally { setBusy(''); }
  };
  const replace = async (f: File, kind: RepKind) => {
    setBusy('up');
    try {
      const r = await uploadDoc(p.id, ck, kind, f);
      const dv = JSON.parse(JSON.stringify(p.docver || {})) as DocVerMap;
      if (!dv[ck] || !dv[ck].length || (dv[ck].length === 1 && dv[ck][0].kind === 'auto' && !dv[ck][0].s3doc)) dv[ck] = [{ ...cur }];
      const prev = dv[ck][dv[ck].length - 1];
      const n = dv[ck].length + 1;
      const at = new Date().toISOString();
      const ver: DocVer = kind === 'edit'
        ? { label: '수정본 v' + n, doc: r.doc, pdf: r.pdf, kind: 'edit', s3doc: r.s3doc, s3pdf: r.s3pdf, at }
        : kind === 'seal'
          ? { label: '날인본 v' + n, doc: prev.doc, pdf: r.pdf, kind: 'seal', s3doc: prev.s3doc || null, s3pdf: r.s3pdf, at }
          : { label: 'PDF만 교체 v' + n, doc: prev.doc, pdf: r.pdf, kind: 'pdfonly', s3doc: prev.s3doc || null, s3pdf: r.s3pdf, at };
      dv[ck] = [...dv[ck], ver];
      onPatch({ docver: dv });
      toast(kind === 'edit'
        ? (r.converted ? '수정본 교체됨 · PDF 재변환 완료' : '⚠ 수정본 저장 · PDF 변환 실패 — PDF를 직접 올려주세요')
        : kind === 'seal' ? '날인본(PDF) 교체됨' : '⚠ PDF만 교체됨 · Word와 불일치');
    } catch (e) { toast((e as Error).message); } finally { setBusy(''); }
  };
  const openSign = async () => {
    if (busy) return;
    setBusy('sign');
    try {
      try { localStorage.removeItem('pdf_edit_url'); } catch (e) { /* noop */ }
      const v = await ensureBondReal(p, co, k, onPatch, toast);
      if (!v.s3pdf) { toast('PDF가 없습니다 — 먼저 생성하세요'); return; }
      const blob = await fetchFileBlob(v.s3pdf, bondFileName(co, k, 'pdf'));
      setEditUrl(URL.createObjectURL(blob));
    } catch (e) { toast((e as Error).message); } finally { setBusy(''); }
  };
  const closeEditor = () => { if (editUrl) URL.revokeObjectURL(editUrl); setEditUrl(null); };
  const editorModal = editUrl ? (
    <PdfEditor
      fileUrl={editUrl}
      hideScan
      onSaveBlob={async (blob: Blob) => { await replace(new File([blob], bondFileName(co, k, 'pdf'), { type: 'application/pdf' }), 'seal'); }}
      onSaved={closeEditor}
      onClose={closeEditor}
    />
  ) : null;

  const wordBadge = !real
    ? <span className="pill p-gray">미생성 · 클릭 시 생성</span>
    : cur.kind === 'auto' ? <span className="pill p-green">자동 생성됨</span> : <span className="pill p-blue">수정본 교체됨</span>;
  const needSeal = seal && cur.kind !== 'seal' ? <span className="pill p-amber">날인 필요</span> : null;
  const sealDone = cur.kind === 'seal' ? <span className="pill p-blue">✍ 서명 완료파일</span> : null;

  return (
    <div className="codoc">
      <div className="codoc-h"><span>{BONDDOCS[k].title}</span><span className="pill p-gray" style={{ fontSize: 10 }}>자동 생성</span></div>
      <div className="s3file">
        <span className="rf">📄 {bondFileName(co, k, 'docx')}</span>{wordBadge}{needSeal}{sealDone}<span className="grow" />
        <button className="rbtn" disabled={!!busy} onClick={() => dl('doc')}>{busy === 'doc' ? '생성 중…' : '⬇ Word'}</button>
        <UpLabel text={busy === 'up' ? '업로드 중…' : '수정본 교체'} disabled={!!busy} onUp={(f) => replace(f, 'edit')} />
      </div>
      <div className="s3file pdf">
        <span className="rf">📕 {bondFileName(co, k, 'pdf')}</span>
        {cur.kind === 'seal' ? <span className="pill p-blue">✍ 서명 완료파일</span> : real ? <span className="pill p-gray">PDF 변환됨</span> : <span className="pill p-gray">미생성</span>}
        <span className="grow" />
        <button className="rbtn" disabled={!!busy} onClick={pv}>{busy === 'pv' ? '여는 중…' : '👁 미리보기'}</button>
        <button className="rbtn" disabled={!!busy} onClick={() => dl('pdf')}>{busy === 'pdf' ? '생성 중…' : '⬇ PDF'}</button>
        <button className="rbtn" disabled={!!busy} onClick={openSign} title="고객이 드로잉패드로 직접 서명 → 날인본으로 저장">{busy === 'sign' ? '여는 중…' : '✍ 서명'}</button>
        <UpLabel text={busy === 'up' ? '업로드 중…' : '날인본 업로드(PDF)'} disabled={!!busy} onUp={(f) => replace(f, 'seal')} />
      </div>
      {editorModal}
    </div>
  );
}
